import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/hooks/use-theme";
import { AuthProvider } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/components/app/ProtectedRoute";
import { RequirePlan } from "@/components/app/RequirePlan";
import { AdminRoute } from "@/components/admin/AdminRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index.tsx";
import PwaInstallPrompt from "./components/PwaInstallPrompt";

// /app routes — carregadas estaticamente para evitar falha de chunk lazy
// durante a navegação interna (ex.: Combinações -> Frequência) em clientes
// com cache antigo/PWA após deploy.
import AppLayout from "./pages/app/AppLayout";
import Dashboard from "./pages/app/Dashboard";
import Historico from "./pages/app/Historico";
import Probabilidades from "./pages/app/Probabilidades";
import Gerador from "./pages/app/Gerador";
import Exportar from "./pages/app/Exportar";
import Frequencia from "./pages/app/Frequencia";
import Jogos from "./pages/app/Jogos";
import DNA from "./pages/app/DNA";
import GeradorInteligente from "./pages/app/GeradorInteligente";
import Coach from "./pages/app/Coach";
import Estatisticas from "./pages/app/Estatisticas";
import Boloes from "./pages/app/Boloes";
import BolaoNovoPage from "./pages/app/BolaoNovoPage";
import BolaoDetalhePage from "./pages/app/BolaoDetalhePage";
import BolaoConferenciasPage from "./pages/app/BolaoConferenciasPage";
import Combinacoes from "./pages/app/Combinacoes";
import Alertas from "./pages/app/Alertas";
import MinhaAssinatura from "./pages/app/MinhaAssinatura";
import Suporte from "./pages/app/Suporte";

// Public/auxiliary pages — lazy
const Entrar = lazy(() => import("./pages/Entrar.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Planos = lazy(() => import("./pages/Planos.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const QuemSomos = lazy(() => import("./pages/QuemSomos.tsx"));
const Alerta = lazy(() => import("./pages/Alerta.tsx"));
const Termos = lazy(() => import("./pages/Termos.tsx"));

// /admin routes — lazy
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminClientes = lazy(() => import("./pages/admin/Clientes"));
const AdminAssinaturas = lazy(() => import("./pages/admin/Assinaturas"));
const AdminLogs = lazy(() => import("./pages/admin/Logs"));
const AdminCalendario = lazy(() => import("./pages/admin/Calendario"));
const AdminOperacoes = lazy(() => import("./pages/admin/Operacoes"));
const AdminConfiguracoes = lazy(() => import("./pages/admin/Configuracoes"));
const AdminSuporte = lazy(() => import("./pages/admin/Suporte"));
const AdminIA = lazy(() => import("./pages/admin/IA"));

const queryClient = new QueryClient();

const AppRoutes = () => {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/entrar" element={<Entrar />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/planos" element={<Planos />} />
        <Route path="/checkout/retorno" element={<CheckoutReturn />} />
        <Route path="/quem-somos" element={<QuemSomos />} />
        <Route path="/alerta" element={<Alerta />} />
        <Route path="/termos" element={<Termos />} />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="historico" element={<Historico />} />
          <Route path="frequencia" element={<Frequencia />} />
          <Route path="probabilidades" element={<Probabilidades />} />
          <Route path="gerador" element={<Gerador />} />
          <Route
            path="gerador-inteligente"
            element={
              <RequirePlan
                tier="pro"
                featureName="Gerador Inteligente"
                description="Algoritmos avançados de geração com filtros estatísticos. Disponível nos planos pagos."
              >
                <GeradorInteligente />
              </RequirePlan>
            }
          />
          <Route
            path="dna"
            element={
              <RequirePlan
                tier="pro"
                featureName="DNA do jogo"
                description="Análise profunda de padrões dos sorteios. Disponível nos planos pagos."
              >
                <DNA />
              </RequirePlan>
            }
          />
          <Route path="coach" element={<Coach />} />
          <Route
            path="estatisticas"
            element={
              <RequirePlan
                tier="pro"
                featureName="Estatísticas avançadas"
                description="Painel completo com correlações, ciclos e tendências. Disponível nos planos Pro e Premium."
              >
                <Estatisticas />
              </RequirePlan>
            }
          />
          <Route path="jogos" element={<Jogos />} />
          <Route
            path="boloes"
            element={
              <RequirePlan
                tier="premium"
                featureName="Bolões"
                description="Crie bolões com participantes, cotas e gestão de prêmios. Exclusivo do plano Premium."
              >
                <Boloes />
              </RequirePlan>
            }
          />
          <Route
            path="boloes/novo"
            element={
              <RequirePlan tier="premium" featureName="Bolões">
                <BolaoNovoPage />
              </RequirePlan>
            }
          />
          <Route
            path="boloes/conferencias"
            element={
              <RequirePlan tier="premium" featureName="Bolões">
                <BolaoConferenciasPage />
              </RequirePlan>
            }
          />
          <Route
            path="boloes/:id"
            element={
              <RequirePlan tier="premium" featureName="Bolões">
                <BolaoDetalhePage />
              </RequirePlan>
            }
          />
          <Route
            path="combinacoes"
            element={
              <RequirePlan
                tier="pro"
                featureName="Combinações"
                description="Gere combinações de jogos com diferentes estratégias e exporte em PDF/CSV. Disponível nos planos Pro e Premium."
              >
                <Combinacoes />
              </RequirePlan>
            }
          />
          <Route path="exportar" element={<Exportar />} />
          <Route path="alertas" element={<Alertas />} />
          <Route path="assinatura" element={<MinhaAssinatura />} />
          <Route path="suporte" element={<Suporte />} />
        </Route>
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="clientes" element={<AdminClientes />} />
          <Route path="assinaturas" element={<AdminAssinaturas />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="calendario" element={<AdminCalendario />} />
          <Route path="operacoes" element={<AdminOperacoes />} />
          <Route path="ia" element={<AdminIA />} />
          <Route path="configuracoes" element={<AdminConfiguracoes />} />
          <Route path="suporte" element={<AdminSuporte />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <PwaInstallPrompt />
            <AppRoutes />
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
